import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import OnboardingConsent from './OnboardingConsent';

function ProtectedRoute({ children }) {
  const { user, loading, hasAcceptedConsent, acceptConsent } = useAuth();
  const location = useLocation();

  // If we're in an auth callback flow (hash contains session_id), show loading
  const hash = window.location.hash;
  if (hash && hash.includes('session_id=')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If user data was passed from AuthCallback via navigation state
  if (location.state?.user) {
    // Still check consent even for freshly authenticated users
    if (!location.state.user.has_accepted_consent) {
      return (
        <>
          {children}
          <OnboardingConsent 
            onAccept={acceptConsent} 
            userName={location.state.user.name}
          />
        </>
      );
    }
    return children;
  }

  // Check if we just authenticated - be lenient for 30 seconds
  const justAuthenticated = sessionStorage.getItem('just_authenticated');
  const loginTime = sessionStorage.getItem('login_time');
  const timeSinceLogin = loginTime ? (Date.now() - parseInt(loginTime)) : Infinity;
  const cachedUser = localStorage.getItem('firstcut_user');
  
  // Trust localStorage if we recently logged in
  if ((justAuthenticated || timeSinceLogin < 30000) && cachedUser) {
    try {
      const parsedUser = JSON.parse(cachedUser);
      if (!parsedUser.has_accepted_consent) {
        return (
          <>
            {children}
            <OnboardingConsent 
              onAccept={acceptConsent} 
              userName={parsedUser.name}
            />
          </>
        );
      }
      return children;
    } catch {
      // Invalid cached data, fall through
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Also check localStorage as fallback
  if (!user && cachedUser) {
    try {
      JSON.parse(cachedUser);
      return children; // Trust cached user temporarily
    } catch {
      // Invalid cached data
    }
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Show consent modal if user hasn't accepted
  if (!hasAcceptedConsent) {
    return (
      <>
        {children}
        <OnboardingConsent 
          onAccept={acceptConsent} 
          userName={user.name}
        />
      </>
    );
  }

  return children;
}

export default ProtectedRoute;
